import templateData from './sourceTemplates.json';

export interface SourceFieldStorage {
  /** Database table (e.g. "vendors_vendor", "vendors_vendoralertypealerttypes") */
  table: string;
  /** Column name (e.g. "name", "data", "alert_types_id") */
  column: string;
  /** Dot-path within a JSONB column (e.g. "apis.announce.uri.host") */
  path?: string;
  /** For alert_types fields: which scope this belongs to ("alert" | "announce") */
  scope?: string;
}

export interface SourceTemplateField {
  /** Form field name */
  name: string;
  /** Display label */
  label: string;
  /** Placeholder text (not used for toggle/alert_types) */
  placeholder?: string;
  /** Input type */
  type:
    | 'text'
    | 'url'
    | 'number'
    | 'password'
    | 'textarea'
    | 'toggle'
    | 'alert_types'
    | 'command_templates'
    | 'select'
    | 'cleartouch_portal'
    | 'plugin_download'
    | 'connection_test';
  /** Whether the field is required */
  required?: boolean;
  /** Help text shown below the field */
  help?: string;
  /** Default checked state for toggle fields */
  defaultChecked?: boolean;
  /** Parent toggle field name — field is hidden when parent is off */
  parent?: string;
  /**
   * Show this field only while another field holds a given value — `parent`
   * for a select rather than a toggle.
   *
   * Used where a choice changes which fields EXIST rather than merely enabling
   * a section: a source we pull from and a source that pushes to us share
   * almost no configuration, and requiring one mode's fields is what makes the
   * other unsaveable. A hidden field is skipped by validation and left out of
   * the payload, exactly as a toggle's children are.
   */
  showWhen?: { field: string; equals: string };
  /** Options for select fields */
  options?: { label: string; value: string }[];
  /**
   * Initial value for a select in ADD mode.
   *
   * A native <select> with no `defaultValue` silently selects its FIRST
   * option, so before this the Verkada auth mode defaulted to `token` only
   * because `token` happened to be listed first — reordering the options
   * would have flipped every new source to `legacy`. Declaring it makes the
   * default explicit, orderable-independent and unit-testable.
   *
   * Never applied in EDIT mode: the stored value wins, so existing rows are
   * not silently migrated.
   */
  defaultValue?: string;
  /** Downloadable plugin files for plugin_download type */
  pluginFiles?: { label: string; path: string }[];
  /** Source code identifier for connection_test probe (e.g. 'verkada') */
  testSource?: string;
  /**
   * Save-first architecture (ADR-067-A Amendment 1 §1.1): probe runs against saved
   * configuration and accepts no credentials in request body. Disables connection test
   * before the source is saved.
   */
  saveFirst?: boolean;
  /** Where this field's value is persisted */
  storage?: SourceFieldStorage;
}

export interface SourceTemplateSection {
  /** Section identifier */
  id: string;
  /** Display label (e.g. "Announce", "Streaming", "Alert") */
  label: string;
  /** Fields within this section */
  fields: SourceTemplateField[];
}

export interface SourceTemplate {
  /** Unique identifier for this template */
  id: string;
  /** Display name */
  name: string;
  /** Short description */
  description: string;
  /** Top-level fields not belonging to any section */
  fields?: SourceTemplateField[];
  /** Grouped sections with labelled field groups */
  sections?: SourceTemplateSection[];
  /** Whether this template supports alert type assignment */
  supportsAlertTypes?: boolean;
  /** Default values to include in the API payload */
  defaults: {
    announce?: { active?: boolean };
    stream?: { active?: boolean };
    alert?: { active?: boolean; can_restart?: boolean };
    event?: { active?: boolean };
    client?: { active?: boolean };
  };
}

export const SOURCE_TEMPLATES: SourceTemplate[] =
  templateData as SourceTemplate[];

/**
 * Derive the set of vendor APIs a template configures.
 *
 * APIs come from two places in the template definition:
 *   1. Field storage paths under `apis.<api>.*` — every field that writes
 *      into `vendors_vendor.data.apis.<api>.*` indicates the template
 *      configures that API.
 *   2. The `defaults` block — keys with `{ active: true }` indicate the
 *      template enables that API by default (e.g. `defaults.announce.active`).
 *
 * Used by the Add Source picker to group templates into "Rosters" vs
 * "All Others" and to power the search filter (matching by name OR API).
 */
export function getTemplateApis(template: SourceTemplate): string[] {
  const apis = new Set<string>();

  const visitField = (f: SourceTemplateField) => {
    const path = f.storage?.path;
    if (!path) return;
    const [head, api] = path.split('.');
    if (head === 'apis' && api) apis.add(api);
  };

  for (const f of template.fields ?? []) visitField(f);
  for (const s of template.sections ?? []) {
    for (const f of s.fields ?? []) visitField(f);
  }

  const d = template.defaults ?? {};
  for (const key of [
    'announce',
    'stream',
    'alert',
    'event',
    'client',
  ] as const) {
    if (d[key]?.active) apis.add(key);
  }

  return [...apis].sort();
}

/**
 * The initial value for a `select` in the source form.
 *
 * 🚨 The stored value ALWAYS wins. This is the rule that keeps an existing row
 * from being silently migrated: a Verkada source saved with
 * `auth_mode: "legacy"` must still render Legacy after `token` became the
 * default for NEW sources, or opening an unrelated field on that source and
 * saving would quietly flip its auth mode.
 *
 * `field.defaultValue` therefore applies only when the edit value is empty —
 * i.e. on add. Returning `undefined` (not `''`) hands the native <select> back
 * to its own first-option behaviour, which is what callers passed before.
 *
 * Extracted from the render so the precedence is testable without mounting
 * the whole SourcesPage.
 */
export function resolveSelectInitialValue(
  editValue: string | undefined | null,
  field: Pick<SourceTemplateField, 'defaultValue'>,
): string | undefined {
  return editValue || field.defaultValue || undefined;
}

/** Every select's initial value: the template's declared default. */
export function templateSelectDefaults(
  template: SourceTemplate,
): Record<string, string> {
  const defaults: Record<string, string> = {};
  const visit = (field: SourceTemplateField) => {
    if (field.type === 'select' && field.defaultValue) {
      defaults[field.name] = field.defaultValue;
    }
  };
  template.fields?.forEach(visit);
  template.sections?.forEach((s) => s.fields.forEach(visit));
  return defaults;
}

/**
 * Whether a field is part of the form as currently configured.
 *
 * Two independent reasons a field can be absent: its parent toggle is off, or
 * its `showWhen` select holds a different value. A hidden field is skipped by
 * validation and left out of the payload, so a `required` field belonging to
 * the mode the user did NOT choose cannot block the save — which is exactly
 * what made a vendor-push source impossible to create.
 */
export function isFieldShown(
  field: SourceTemplateField,
  state: {
    toggles?: Record<string, boolean>;
    selects?: Record<string, string>;
  },
): boolean {
  if (field.parent && !(state.toggles?.[field.parent] ?? false)) return false;
  if (field.showWhen) {
    return (
      (state.selects?.[field.showWhen.field] ?? '') === field.showWhen.equals
    );
  }
  return true;
}

/** True iff the template configures the OneRoster ingestion API. */
export function isRosterTemplate(template: SourceTemplate): boolean {
  return getTemplateApis(template).includes('roster');
}
